import { useState, useEffect } from "react";
import { useAuth, useSlots, useAdminSlots, useBookings, useCustomer, useIsAdmin, useCreditBalance, CHECKOUT_PENDING_KEY } from "./lib/hooks";
import { Calendar } from "./components/Calendar";
import { AuthForm } from "./components/AuthForm";
import { MyBookings, type MyBookingsSubTab } from "./components/MyBookings";
import { GenerateSlots } from "./components/admin/GenerateSlots";
import { CreateWorkshop } from "./components/admin/CreateWorkshop";
import { ManageCredits } from "./components/admin/ManageCredits";
import { ManageVouchers } from "./components/admin/ManageVouchers";
import { ManageDiscountCodes } from "./components/admin/ManageDiscountCodes";
import { RedeemVoucher, formatWorkshopDate } from "./components/RedeemVoucher";
import type { RedeemResult } from "./components/RedeemVoucher";
import { Modal } from "./components/Modal";

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

type Tab = "calendar" | "bookings" | "voucher" | "generate" | "workshop" | "credits" | "vouchers" | "discounts";

// The active tab lives in the URL hash so a refresh (or a shared link) lands on the
// same tab. Written with replaceState: no scroll jump, no history entries.
const TAB_HASHES: Record<Tab, string> = {
  calendar: "kalendar",
  bookings: "rezervacie",
  voucher: "poukaz",
  generate: "generovat",
  workshop: "workshop",
  credits: "kredit",
  vouchers: "kupony",
  discounts: "zlavy",
};

function tabFromHash(): Tab {
  const hash = window.location.hash.replace(/^#/, "");
  const match = (Object.keys(TAB_HASHES) as Tab[]).find((t) => TAB_HASHES[t] === hash);
  return match ?? "calendar";
}

function hasCheckoutMarker(): boolean {
  try {
    return sessionStorage.getItem(CHECKOUT_PENDING_KEY) !== null;
  } catch {
    return false;
  }
}

/** Reads and clears the marker left by redirectToCheckout(); true when this tab left for Stripe. */
function consumeCheckoutMarker(): boolean {
  const present = hasCheckoutMarker();
  if (present) {
    try {
      sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
    } catch {
      // ignore
    }
  }
  return present;
}

function writeTabHash(tab: Tab) {
  const url = window.location.pathname + window.location.search + "#" + TAB_HASHES[tab];
  window.history.replaceState(null, "", url);
}

export default function App() {
  const { user, loading: authLoading, isPasswordRecovery, signIn, signUp, signOut, resetPassword, updatePassword, clearPasswordRecovery } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin(user?.email ?? undefined);
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  // Fetch slots only once the role is known, so customers never hit /api/admin/*
  // and admins don't load public slots first and then refetch. The role is
  // unknown while the auth session is still resolving, not just during the admin check.
  const roleKnown = !authLoading && !adminLoading;
  const publicSlots = useSlots(weekStart, roleKnown && !isAdmin);
  const adminSlots = useAdminSlots(weekStart, roleKnown && isAdmin);
  const { slots, loading: slotsLoading, refetch } = isAdmin ? adminSlots : publicSlots;
  const { bookings, loading: bookingsLoading, refetch: refetchBookings } = useBookings(user?.id);
  const { customer, refetchCustomer } = useCustomer(user?.id);
  const { balance: creditBalance, transactions: creditTransactions, refetchBalance } = useCreditBalance(user?.id);
  const [tab, setTabState] = useState<Tab>(tabFromHash);
  const [showAuth, setShowAuth] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<{ text: string; kind: "success" | "warning" } | null>(null);
  const [bookingsSubTab, setBookingsSubTab] = useState<MyBookingsSubTab>("reservations");

  const setTab = (next: Tab) => {
    setTabState(next);
    writeTabHash(next);
  };

  // Follow manual hash edits / browser navigation between hashes.
  useEffect(() => {
    const onHashChange = () => setTabState(tabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // A hash can point at a tab the current visitor can't see (logged out, not admin).
  // Once the role is known, fall back to the calendar instead of rendering nothing.
  useEffect(() => {
    if (!roleKnown) return;
    const needsUser = tab === "bookings";
    const needsAdmin = tab === "generate" || tab === "workshop" || tab === "credits" || tab === "vouchers" || tab === "discounts";
    if ((needsUser && !user) || (needsAdmin && !isAdmin)) {
      setTab("calendar");
    }
    // The voucher email links straight to #poukaz: keep the tab and ask for login,
    // so the recipient lands on the redeem form right after signing in.
    if (tab === "voucher" && !user) {
      setShowAuth(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKnown, user, isAdmin]);

  // Handle return from Stripe Checkout: drop the query params, land on "Moje rezervácie"
  // where the paid booking is. A return through the browser Back button carries no
  // query params; it's recognised by the marker redirectToCheckout() left behind.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cameBackFromCheckout = consumeCheckoutMarker();
    if (params.has("session_id")) {
      setPaymentMessage({ text: "Platba úspešná! Vaša rezervácia je potvrdená.", kind: "success" });
      window.history.replaceState({}, "", window.location.pathname);
      setTab("bookings");
    } else if (params.get("payment") === "cancelled") {
      setPaymentMessage({ text: "Platba zrušená. Rezervácia nebola dokončená.", kind: "warning" });
      window.history.replaceState({}, "", window.location.pathname);
      setTab("bookings");
    } else if (cameBackFromCheckout) {
      setPaymentMessage({ text: "Platba nebola dokončená. Rezerváciu môžete zaplatiť v časti Moje rezervácie.", kind: "warning" });
      setTab("bookings");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back from Stripe may restore this page from the back/forward cache instead of
  // loading it: no mount effect runs and the booking popup is still "loading".
  // Reload so the marker is handled by the effect above.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && hasCheckoutMarker()) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (authLoading) {
    return <div className="widget-loading">Načítavam...</div>;
  }

  const handleBooked = () => {
    refetch();
    refetchBookings();
    refetchCustomer();
    refetchBalance();
  };

  const handleVoucherRedeemed = (r: RedeemResult) => {
    if (r.type === "credit") {
      refetchBalance();
      setPaymentMessage({
        text: `Poukaz ${r.code} uplatnený, kredit +${(r.amountCents / 100).toFixed(2).replace(".", ",")} € pripísaný.`,
        kind: "success",
      });
      setBookingsSubTab("credit");
    } else {
      // A seat was booked: same refresh as after a paid booking, show it among reservations.
      handleBooked();
      setPaymentMessage({
        text: `Poukaz ${r.code} uplatnený, ste zapísaný na workshop ${r.kindLabel} ${formatWorkshopDate(r.booked.date)} o ${r.booked.start_time.slice(0, 5)}. Potvrdenie s PIN kódom príde emailom.`,
        kind: "success",
      });
      setBookingsSubTab("reservations");
    }
    setTab("bookings");
  };

  const handleCancelled = () => {
    refetch();
    refetchBookings();
    refetchBalance();
  };

  const userName = user?.user_metadata?.name || user?.email || "";
  const customerCode = customer?.customer_code;
  const confirmedBookings = bookings.filter((b) => b.status === "confirmed");
  const myBookedSlotIds = new Set(confirmedBookings.map((b) => b.slot_id));
  const myBookingGroupMap = new Map(confirmedBookings.map((b) => [b.slot_id, b.booking_group_id]));

  return (
    <div className="booking-widget">
      {/* Header */}
      <header className="widget-header">
        <h1>Rezervácie</h1>
        <div className="header-right">
          {user ? (
            <>
              <nav className="tab-nav">
                <button
                  className={`tab-btn ${tab === "calendar" ? "active" : ""}`}
                  onClick={() => setTab("calendar")}
                >
                  Kalendár
                </button>
                {!isAdmin && (
                  <button
                    className={`tab-btn ${tab === "bookings" ? "active" : ""}`}
                    onClick={() => setTab("bookings")}
                  >
                    Moje rezervácie
                  </button>
                )}
                {!isAdmin && (
                  <button
                    className={`tab-btn ${tab === "voucher" ? "active" : ""}`}
                    onClick={() => setTab("voucher")}
                  >
                    Uplatniť poukaz
                  </button>
                )}
                {isAdmin && (
                  <>
                    <button
                      className={`tab-btn tab-btn-admin ${tab === "generate" ? "active" : ""}`}
                      onClick={() => setTab("generate")}
                    >
                      Generovať sloty
                    </button>
                    <button
                      className={`tab-btn tab-btn-admin ${tab === "workshop" ? "active" : ""}`}
                      onClick={() => setTab("workshop")}
                    >
                      Nový workshop
                    </button>
                    <button
                      className={`tab-btn tab-btn-admin ${tab === "credits" ? "active" : ""}`}
                      onClick={() => setTab("credits")}
                    >
                      Kredit
                    </button>
                    <button
                      className={`tab-btn tab-btn-admin ${tab === "vouchers" ? "active" : ""}`}
                      onClick={() => setTab("vouchers")}
                    >
                      Kupóny
                    </button>
                    <button
                      className={`tab-btn tab-btn-admin ${tab === "discounts" ? "active" : ""}`}
                      onClick={() => setTab("discounts")}
                    >
                      Zľavy
                    </button>
                  </>
                )}
              </nav>
              <span className="user-info">
                {userName}
                {isAdmin && <span className="admin-badge">Admin</span>}
                {customerCode && !isAdmin && <span className="user-code">({customerCode})</span>}
                {creditBalance > 0 && (
                  <span className="credit-badge">Kredit {(creditBalance / 100).toFixed(2).replace(".", ",")} €</span>
                )}
              </span>
              <button className="btn btn-small btn-outline" onClick={signOut}>
                Odhlásiť sa
              </button>
            </>
          ) : (
            <>
              {/* Logged out: the voucher tab is visible so a gift recipient finds it;
                  clicking it opens the login and keeps the tab for after sign-in. */}
              <nav className="tab-nav">
                <button
                  className={`tab-btn ${tab === "calendar" ? "active" : ""}`}
                  onClick={() => setTab("calendar")}
                >
                  Kalendár
                </button>
                <button
                  className={`tab-btn ${tab === "voucher" ? "active" : ""}`}
                  onClick={() => { setTab("voucher"); setShowAuth(true); }}
                >
                  Uplatniť poukaz
                </button>
              </nav>
              <button className="btn btn-primary btn-small" onClick={() => setShowAuth(true)}>
                Prihlásiť sa
              </button>
            </>
          )}
        </div>
      </header>

      {/* Payment status banner */}
      {paymentMessage && (
        <div className={`payment-banner ${paymentMessage.kind}`}>
          {paymentMessage.text}
          <button className="banner-close" onClick={() => setPaymentMessage(null)}>×</button>
        </div>
      )}

      {/* Content */}
      <main className="widget-content">
        {tab === "calendar" && (
          <>
            {slotsLoading && slots.length === 0 ? (
              <div className="loading">Načítavam sloty...</div>
            ) : (
              <>
                <Calendar
                  slots={slots}
                  weekStart={weekStart}
                  onWeekChange={setWeekStart}
                  isLoggedIn={!!user}
                  isAdmin={isAdmin}
                  isAdminManageMode={isAdmin}
                  myBookedSlotIds={myBookedSlotIds}
                  myBookingGroupMap={myBookingGroupMap}
                  creditBalance={creditBalance}
                  onRefetch={handleBooked}
                  onCancelled={handleCancelled}
                  onLoginPrompt={() => setShowAuth(true)}
                />
              </>
            )}
            {!user && (
              <div className="login-hint">
                Pre vytvorenie rezervácie sa najprv{" "}
                <button className="link-btn" onClick={() => setShowAuth(true)}>
                  prihláste
                </button>.
              </div>
            )}
          </>
        )}

        {tab === "bookings" && user && (
          <MyBookings
            bookings={bookings}
            customer={customer}
            loading={bookingsLoading}
            creditBalance={creditBalance}
            creditTransactions={creditTransactions}
            onCancelled={handleCancelled}
            subTab={bookingsSubTab}
            onSubTabChange={setBookingsSubTab}
            onOpenVoucher={() => setTab("voucher")}
          />
        )}

        {tab === "voucher" && !isAdmin && (
          user ? (
            <RedeemVoucher onRedeemed={handleVoucherRedeemed} />
          ) : (
            <div className="login-hint">
              Na uplatnenie poukazu sa najprv{" "}
              <button className="link-btn" onClick={() => setShowAuth(true)}>prihláste alebo zaregistrujte</button>.
            </div>
          )
        )}

        {tab === "generate" && isAdmin && (
          <GenerateSlots onGenerated={refetch} />
        )}

        {tab === "workshop" && isAdmin && (
          <CreateWorkshop onCreated={refetch} />
        )}

        {tab === "credits" && isAdmin && (
          <ManageCredits />
        )}

        {tab === "vouchers" && isAdmin && (
          <ManageVouchers />
        )}

        {tab === "discounts" && isAdmin && (
          <ManageDiscountCodes />
        )}

      </main>

      {/* Auth Modal — login/register/forgot password */}
      {showAuth && !user && (
        <Modal>
          <div className="auth-modal-overlay" onClick={() => setShowAuth(false)}>
            <div onClick={(e) => e.stopPropagation()}>
              <AuthForm
                onSignIn={async (e, p) => {
                  const res = await signIn(e, p);
                  if (!res.error) setShowAuth(false);
                  return res;
                }}
                onSignUp={signUp}
                onResetPassword={resetPassword}
                onClose={() => setShowAuth(false)}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Password Recovery Modal — shown when user clicks reset link from email */}
      {isPasswordRecovery && (
        <Modal>
          <div className="auth-modal-overlay" onClick={() => { clearPasswordRecovery(); signOut(); }}>
            <div onClick={(e) => e.stopPropagation()}>
              <AuthForm
                onSignIn={signIn}
                onSignUp={signUp}
                onResetPassword={resetPassword}
                onUpdatePassword={async (p) => {
                  const res = await updatePassword(p);
                  if (!res.error) {
                    setTimeout(() => clearPasswordRecovery(), 2500);
                  }
                  return res;
                }}
                initialMode="new-password"
                onClose={() => { clearPasswordRecovery(); signOut(); }}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
